import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import { attributionPillSx, textLinkSx } from './CaseStudies';
import CaseStudyNextSteps from '../components/CaseStudyNextSteps';
import MultiSiteDiagram from '../components/MultiSiteDiagram';
import { cardSx } from '../components/stitch/Section';
import TestimonialQuote from '../components/TestimonialQuote';
import { CaseStudy, caseStudies, caseStudyPath } from '../data/CaseStudies';
import { testimonialForStudy } from '../data/Testimonials';
import { tokens } from '../theme';

// Built to the Stitch "Case Study Detail" screen (project 1587770953174176333). The screen's
// stock photo, architecture panel and placeholder testimonial are left out: they were not
// real project material.
function CaseStudyPage({ study }: { study: CaseStudy }) {
  const others = caseStudies.filter((item) => item.id !== study.id);
  const testimonial = testimonialForStudy(study.id);
  const story = [
    ['The need', study.problem],
    ['The work', study.work],
    ['What changed', study.result],
  ] as const;
  return (
    <Container>
      <Box sx={{ pt: { xs: 3, md: 4 } }}>
        <Box
          component={Link}
          to='/case-studies'
          sx={textLinkSx}
        >
          <ArrowBackIcon
            aria-hidden='true'
            sx={{ fontSize: 18 }}
          />
          All CMS case studies
        </Box>
      </Box>

      <Box
        component='article'
        aria-labelledby={`${study.id}-title`}
        sx={{ minWidth: 0 }}
      >
        <Box sx={{ py: { xs: 4, md: 7 }, maxWidth: '56rem' }}>
          <Typography
            variant='labelCaps'
            component='p'
            sx={attributionPillSx}
          >
            {study.attribution}
          </Typography>
          <Typography
            component='h1'
            id={`${study.id}-title`}
            variant='h1'
            aria-label={`${study.title} — ${study.attribution}`}
            sx={{ mt: 2.5, overflowWrap: 'anywhere' }}
          >
            {study.title}
          </Typography>
        </Box>

        <Box
          component='dl'
          sx={{
            m: 0,
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            bgcolor: tokens.surfaceLow,
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
            gap: { xs: 2, md: 3 },
          }}
        >
          {story.map(([label, value], index) => {
            const outcome = label === 'What changed';
            return (
              <Box
                key={label}
                sx={{
                  ...cardSx,
                  p: { xs: 3, md: 4 },
                  minWidth: 0,
                  ...(outcome && {
                    bgcolor: tokens.surfaceHigh,
                    borderColor: tokens.surfaceHigh,
                    borderTop: `4px solid ${tokens.secondary}`,
                  }),
                }}
              >
                <Typography
                  variant='labelCaps'
                  component='p'
                  aria-hidden='true'
                  sx={{ color: outcome ? tokens.accentText : tokens.onSurfaceVariant }}
                >
                  Part {String(index + 1).padStart(2, '0')}
                </Typography>
                <Typography
                  component='dt'
                  variant='labelCaps'
                  sx={{
                    display: 'block',
                    mt: 2,
                    color: outcome ? tokens.primary : tokens.accentText,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  component='dd'
                  variant={index === 1 ? 'body2' : 'bodyLg'}
                  sx={{
                    m: 0,
                    mt: 1.5,
                    color: outcome ? tokens.primary : tokens.onSurface,
                    fontWeight: outcome ? 500 : undefined,
                  }}
                >
                  {value}
                </Typography>
              </Box>
            );
          })}
        </Box>
        {study.diagram && <MultiSiteDiagram />}
        {testimonial && (
          <Box sx={{ mt: { xs: 4, md: 6 } }}>
            <TestimonialQuote testimonial={testimonial} />
          </Box>
        )}
      </Box>

      <Box
        component='nav'
        aria-labelledby='more-case-studies'
        sx={{
          mt: { xs: 6, md: 8 },
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          bgcolor: tokens.surfaceLow,
        }}
      >
        <Typography
          variant='labelCaps'
          component='p'
          sx={{ color: tokens.accentText }}
        >
          Case studies
        </Typography>
        <Typography
          id='more-case-studies'
          component='h2'
          variant='h3'
          sx={{ mt: 1 }}
        >
          More case studies
        </Typography>
        <Box
          component='ul'
          sx={{
            listStyle: 'none',
            m: 0,
            mt: 3,
            p: 0,
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          {others.map((item) => (
            <Box
              component='li'
              key={item.id}
              sx={{ minWidth: 0 }}
            >
              <Box
                component={Link}
                to={caseStudyPath(item.id)}
                sx={{
                  ...cardSx,
                  height: '100%',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 3,
                  color: tokens.primary,
                  fontFamily: '"Manrope Variable", sans-serif',
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  lineHeight: 26 / 18,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline', borderColor: tokens.secondary },
                }}
              >
                <Box
                  component='span'
                  sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                >
                  {item.title}
                </Box>
                <ArrowForwardIcon
                  aria-hidden='true'
                  sx={{ color: tokens.outline, flexShrink: 0 }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <CaseStudyNextSteps />
    </Container>
  );
}

export default CaseStudyPage;
